import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Euro, FileText, TrendingUp, Plus, ArrowUpRight } from "lucide-react";
import BillingBanner from "@/components/BillingBanner";
import PricingNotice from "@/components/PricingNotice";
import MagicEventButton from "@/components/MagicEventButton";

const EVENT_LABELS = {
  matrimonio: "Matrimonio", addio_celibato: "Addio al celibato",
  addio_nubilato: "Addio al nubilato", laurea: "Festa di laurea",
  compleanno: "Compleanno", battesimo: "Battesimo", cresima: "Cresima",
  comunione: "Comunione", fidanzamento: "Fidanzamento",
  cenone_capodanno: "Cenone di Capodanno", pranzo_natale: "Pranzo di Natale",
  pranzo_pasqua: "Pranzo di Pasqua", pranzo_pasquetta: "Pranzo di Pasquetta",
  pranzo_ferragosto: "Pranzo di Ferragosto", cena_ferragosto: "Cena di Ferragosto",
  altro: "Altro",
};

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ active_events: 0, quoted_events: 0, total_events: 0, total_revenue: 0, total_cost: 0, next_events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats").then((r) => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const kpi = [
    { label: "Eventi attivi", value: stats.active_events, icon: Calendar, color: "#2563EB" },
    { label: "Preventivi inviati", value: stats.quoted_events, icon: FileText, color: "#0F2741" },
    { label: "Fatturato previsto", value: fmtEur(stats.total_revenue), icon: TrendingUp, color: "#0EA5E9" },
    { label: "Costi totali", value: fmtEur(stats.total_cost), icon: Euro, color: "#F59E0B" },
  ];

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Dashboard</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Buongiorno, {user?.name?.split(" ")[0] || "Ospite"}.</h1>
          <p className="text-slate-500 mt-2">Ecco lo stato dei tuoi eventi e dei tuoi preventivi.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <MagicEventButton />
          <button onClick={() => navigate("/eventi/nuovo")} data-testid="btn-new-event" className="btn-primary">
            <Plus className="w-4 h-4" /> Nuovo evento
          </button>
        </div>
      </div>

      {/* Billing soft paywall */}
      <BillingBanner />
      <PricingNotice />

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {kpi.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-white rounded-xl p-6 border border-slate-200 card-hover">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k.label}</p>
                <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${k.color}15` }}>
                  <Icon className="w-4 h-4" style={{ color: k.color }} />
                </div>
              </div>
              <p className="font-display text-3xl text-[#0F2741] mt-4">{k.value}</p>
            </div>
          );
        })}
      </div>

      {/* Next events */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-ui font-semibold text-lg text-[#0F2741]">Prossimi eventi</h2>
            <p className="text-xs text-slate-500 mt-1">I tuoi appuntamenti più ravvicinati</p>
          </div>
          <button onClick={() => navigate("/eventi")} className="btn-ghost">
            Tutti gli eventi <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-slate-100" data-testid="next-events-list">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Caricamento...</div>
          ) : stats.next_events.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun evento programmato.</p>
              <button onClick={() => navigate("/eventi/nuovo")} className="btn-primary mt-4">Crea il primo evento</button>
            </div>
          ) : (
            stats.next_events.map((e) => (
              <div key={e.id} onClick={() => navigate(`/eventi/${e.id}`)} className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0F2741] grid place-items-center text-white font-display text-lg">
                    {e.event_date?.split("-")[2] || "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F2741] font-ui">{EVENT_LABELS[e.event_type] || e.event_type}</p>
                    <p className="text-xs text-slate-500">{e.client_name || "Cliente"} · {e.guests} ospiti · {e.event_date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg text-[#0F2741]">{fmtEur(e.final_price)}</p>
                  <p className="text-xs text-slate-400 uppercase">{e.status}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
